
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Plus, CreditCard } from "lucide-react";

interface CreditsProps {
  credits: number;
  onPurchase: (amount: number) => void;
}

const Credits = ({ credits, onPurchase }: CreditsProps) => {
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState<number | null>(null);

  const creditBundles = [
    { amount: 10, price: 10, bonus: 0 },
    { amount: 22, price: 20, bonus: 2 },
    { amount: 55, price: 50, bonus: 5 },
  ];

  const handlePurchaseCredits = () => {
    if (!selectedBundle) return;
    
    setIsProcessing(true);
    // Simulate payment processing with Stripe
    setTimeout(() => {
      setIsProcessing(false);
      onPurchase(selectedBundle);
      toast({
        title: "Credits Purchased",
        description: `You've successfully purchased ${selectedBundle} credits!`,
      });
    }, 2000);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-medium">Your Credits</h3>
          <p className="text-gray-600">Use credits to optimize resumes</p>
        </div>
        <div className="flex items-center bg-blue-50 px-3 py-2 rounded-md">
          <span className="text-blue-700 font-medium text-lg mr-1">{credits}</span>
          <span className="text-blue-700">credits available</span>
        </div>
      </div>
      
      <Dialog>
        <DialogTrigger asChild>
          <Button className="w-full">
            <Plus className="mr-2 h-4 w-4" /> Buy More Credits
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Purchase Credits</DialogTitle>
            <DialogDescription>
              Buy credits in bulk and save on fees! Credits don't expire for 12 months.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {creditBundles.map((bundle) => (
              <Card 
                key={bundle.amount} 
                className={`cursor-pointer border-2 transition-all ${
                  selectedBundle === bundle.amount ? "border-blue-500 bg-blue-50" : ""
                }`}
                onClick={() => setSelectedBundle(bundle.amount)}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex justify-between">
                    <span>{bundle.amount} Credits</span>
                    <span>${bundle.price}.00</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-500">
                    {bundle.bonus > 0 ? (
                      <span className="text-green-600 font-medium">
                        Includes {bundle.bonus} bonus credits!
                      </span>
                    ) : (
                      "Best for occasional use"
                    )}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handlePurchaseCredits} 
              disabled={!selectedBundle || isProcessing}
              className="w-full"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {isProcessing ? "Processing..." : `Pay $${selectedBundle ? 
                creditBundles.find(bundle => bundle.amount === selectedBundle)?.price : 0}.00`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Credits;
